<template>
  <div
    class="listing-result"
    :data-listing-id="listing.id"
    :class="`${hoverOff ? 'hoverOff' : ''} ${isSelected ? 'is-selected' : ''}`"
    v-on:mouseenter="$emit('hoverResult', listing.id)"
    v-on:mouseleave="$emit('hoverResult', false)"
  >
    <!-- NOTE: attribute data-id is also in use by analytics for fb ads so do not change it -->
    <a
      class="listing-result__link"
      :id="listing.id"
      :data-id="listing.id"
      @click.prevent="navigateToListingPage(listing.id)"
      :href="getTargetUrl(listing.id)"
      :target="`${embedded ? '_blank' : '_self'}`"
      @focus="$emit('focus')"
    >
      <!-- Image -->
      <keep-alive>
        <div
          :class="`listing-result__image ${listing.imageUrl ? '' : 'placeholder'}`"
          :style="`background-image: url(${listing.imageUrl || 'https://op-koti.fi/assets/image-placeholder2.svg'})`"
        >
          <div class="logo-banner" v-if="isNewsecListing()" v-html="newsecLogo"></div>
          <div class="logo-banner op" v-else></div>
          <div class="listing-result__showings" v-if="showings.length">
            <showings :showings="showings" :show-end-time="true"></showings>
          </div>
        </div>
      </keep-alive>

      <div class="listing-result__detail-row price-and-size">
        <!-- Price -->
        <template v-if="rentalMode === true">
          <span v-if="listing.rentPerMonth" class="listing-result__price">
            {{ $filters.euro(listing.rentPerMonth, { dashify: false, perMonth: true }) }}
          </span>
        </template>
        <template v-else>
          <span v-if="hasPriceOrRent(rentalMode)" class="listing-result__price">
            {{
              $filters.euro(getPriceOrRent(rentalMode), {
                dashify: false,
                perMonth: !hasValidPrice(),
              })
            }}
          </span>
        </template>

        <!-- Size -->
        <template v-if="areaWithUnits">
          <span class="basics-block__value size">
            <span class="listing-result__size">
              <area-with-units :size="areaWithUnits.size" :unit="areaWithUnits.displayUnit"></area-with-units>
            </span>
          </span>
        </template>
      </div>

      <!-- Address and room info -->
      <div class="listing-result__detail-row">
        <span class="listing-result__street">
          {{ listing.location.streetAddress.trim() }}
          <span class="desktop-comma">,</span>
        </span>
        <br />
        <!--
        -->
        <span class="listing-result__district" v-if="listing.location.district">{{ listing.location.district }},</span>
        <!--
        -->
        <span class="listing-result__city" v-if="listing.location.city">
          {{ listing.location.city }}
        </span>
      </div>
      <div class="listing-result__detail-row">
        <div class="listing-result__type-and-rooms">
          <span class="listing-result__type" v-if="listingType">{{ listingType.name }}</span>
        </div>
      </div>
    </a>

    <div class="listing-result__icons">
      <a
        @click="stopBubbling($event)"
        v-if="listing.virtualPresentationUrl"
        :href="listing.virtualPresentationUrl"
        target="_blank"
      >
        <i class="listing-result__icon icon-virtual"></i>
      </a>
      <a @click="stopBubbling($event)" v-if="listing.videoUrl" :href="listing.videoUrl" target="_blank">
        <i class="listing-result__icon icon-videoclip-circled"></i>
      </a>
    </div>
  </div>
</template>
<script>
import config from '@/config';
import listingUtils from '@/app/utils/listing-utils';
import AreaWithUnits from '../area-with-units/area-with-units.vue';
import Showings from '../showings/showings.vue';
import { listing as listingRoute, embeddable } from '@/app/routes';
import NewsecLogo from '@/app/icons/newsec_rgb.svg';

export default {
  name: 'result',
  data: () => ({
    config,
    newsecLogo: NewsecLogo,
    listingRoute,
    elementHasHover: false,
    preventDelayedSelection: false,
    preventDelayedUnselection: false,
    embedded: false,
  }),
  props: {
    listing: {
      type: Object,
      required: true,
    },
    hoverOff: {
      type: Boolean,
    },
    rentalMode: {
      type: Boolean,
      default: undefined,
    },
    isSelected: {
      type: Boolean,
      default: false,
    },
  },
  mounted() {
    this.embedded = this.$route.path.includes(embeddable.path);
  },
  computed: {
    showings() {
      if (!this.listing) {
        return [];
      }
      return this.listing.showings;
    },

    areaWithUnits() {
      if (!this.listing) return null;

      const listingType = listingUtils.getListingTypeById(this.listing.listingType);
      const livingArea = this.listing.housing.livingArea;
      const totalArea = this.listing.housing.totalArea;
      const siteArea = this.listing.housing.siteArea;

      return listingUtils.getPrimaryAreaWithUnits(listingType, livingArea, totalArea, siteArea);
    },
    listingType() {
      if (!this.listing) return null;
      return listingUtils.getListingTypeById(this.listing.listingType);
    },
  },
  methods: {
    isNewsecListing() {
      return this.listing.source === config.LISTING_SOURCES.NEWSEC;
    },
    hasValidPrice() {
      const hasValidDebtFreePrice =
        this.listing.pricing.debtFreePrice && parseInt(this.listing.pricing.debtFreePrice, 10) > 1;
      const hasValidPrice = this.listing.pricing.price && parseInt(this.listing.pricing.price, 10) > 1;
      return hasValidDebtFreePrice || hasValidPrice;
    },
    hasValidRent() {
      return this.listing.pricing.rentPerMonth && parseInt(this.listing.pricing.rentPerMonth, 10) > 1;
    },
    hasPriceOrRent(rentalMode) {
      if (rentalMode === true) {
        return this.hasValidRent();
      } else if (rentalMode === false) {
        return this.hasValidPrice();
      }
      return this.hasValidPrice() || this.hasValidRent();
    },
    getPriceOrRent(rentalMode) {
      if (rentalMode === true) {
        if (this.hasValidRent()) {
          return this.listing.pricing.rentPerMonth;
        }
      } else if (rentalMode === false) {
        if (this.hasValidPrice()) {
          return this.listing.pricing.debtFreePrice || this.listing.pricing.price;
        }
      } else {
        if (this.hasValidPrice()) {
          return this.listing.pricing.debtFreePrice || this.listing.pricing.price;
        } else if (this.hasValidRent()) {
          return this.listing.pricing.rentPerMonth;
        }
      }
      return null;
    },
    getWeekday(timestamp) {
      return timestamp && new Date(timestamp) ? config.weekdays[new Date(timestamp).getDay()] : '';
    },
    stopBubbling(event) {
      event.stopPropagation();
    },
    navigateToListingPage(listingId) {
      if (this.embedded) {
        return;
      }
      this.$router.push(`${listingRoute.path}/${listingId}`);
    },
    getTargetUrl(id) {
      const url = `${listingRoute.path}/${id}`;
      // add query string to url only on embedded view
      return this.embedded ? `${url}?cmpid=.hapankki..huom.metsagroup.muu.embedded_listing.ha_jn_partnership` : url;
    },
  },
  components: {
    Showings,
    AreaWithUnits,
  },
};
</script>
